import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export type ExpertReviewEvidence = 'OBSERVED' | 'MIXED' | 'CLUSTER_CARRIED';
export type ExpertReviewStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'ADJUSTED'
  | 'REJECTED';
export type ExpertReviewPropertyType = 'BUILDING' | 'LAND';
export type ExpertReviewQuarterStatus = 'LIVE' | 'CLOSED';
export type ExpertReviewFilterKey =
  | 'needs'
  | 'high'
  | 'approved'
  | 'adjusted'
  | 'rejected'
  | 'all';

export interface ExpertReviewDealObject {
  propertyType: ExpertReviewPropertyType;
  sizeSqm: number;
  pricePerSqm: number;
}

export interface ExpertReviewAreaDetailsObject {
  area: ExpertReviewAreaObject | null;
  priceHistory: Array<number>;
  deals: Array<ExpertReviewDealObject>;
  clusterDeals: Array<ExpertReviewClusterDealObject>;
}

export interface ExpertReviewAreaObject {
  id: number;
  titleEn: string;
  titleAr: string;
  clusterId: number;
  pricePerSqm: number | null;
  publishedPrice: number | null;
  previousPricePerSqm: number | null;
  risk: string | null;
  qoqChange: number | null;
  yoyChange: number | null;
  dealsCount: number;
  clusterDealsCount: number;
  evidence: ExpertReviewEvidence;
  isThinCluster: boolean;
  status: ExpertReviewStatus;
  isFinal: boolean;
  rejectReasonId: number | null;
  rejectReasonNote: string | null;
  expertValue: number | null;
}

export interface ExpertReviewRejectReasonObject {
  id: number;
  nameEn: string;
  nameAr: string;
  slug: string;
}

export interface ExpertReviewClusterDealObject extends ExpertReviewDealObject {
  areaTitleEn: string;
  areaTitleAr: string;
}

export interface ExpertReviewQuarterObject {
  code: string;
  isFinal: boolean;
  elapsedPercentage: number;
  thinClusterThreshold: number;
}

export type ExpertReviewCounts = Record<string, number>;

export type ExpertReviewDetailsStatus = 'loading' | 'ready' | 'error';
export type ExpertReviewReasonsStatus = 'loading' | 'ready' | 'error';

export interface ExpertReviewListingResponse {
  data: Array<ExpertReviewAreaObject>;
  counts: ExpertReviewCounts;
  quarter: ExpertReviewQuarterObject;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
}

// what each console tab asks the board for
const FILTER_PARAMS: Record<
  ExpertReviewFilterKey,
  { status?: string; highRisk?: boolean }
> = {
  needs: { status: 'PENDING' },
  high: { status: 'PENDING', highRisk: true },
  approved: { status: 'APPROVED' },
  adjusted: { status: 'ADJUSTED' },
  rejected: { status: 'REJECTED' },
  all: {},
};

let detailsRequest = 0;

const commitRequestError = (context, error) => {
  const response = error?.response;
  context.commit(Mutations.SET_ERROR, {
    [response?.statusText || 'Error']: [
      response?.data?.message || 'SOMETHING_WENT_WRONG',
    ],
  });
};

@Module
export default class ExpertReviewModule extends VuexModule {
  items: Array<ExpertReviewAreaObject> = [];
  details: ExpertReviewAreaDetailsObject = {
    area: null,
    priceHistory: [],
    deals: [],
    clusterDeals: [],
  };
  detailsStatus: ExpertReviewDetailsStatus = 'loading';
  rejectReasons: Array<ExpertReviewRejectReasonObject> = [];
  reasonsStatus: ExpertReviewReasonsStatus = 'loading';
  counts: ExpertReviewCounts = {
    PENDING: 0,
    APPROVED: 0,
    ADJUSTED: 0,
    REJECTED: 0,
    high: 0,
    all: 0,
  };
  quarter: ExpertReviewQuarterObject = {
    code: '',
    isFinal: false,
    elapsedPercentage: 0,
    thinClusterThreshold: 0,
  };
  filter: ExpertReviewFilterKey = 'needs';
  quarterStatus: ExpertReviewQuarterStatus = 'LIVE';
  totalItems = 0;
  currentPage = 1;
  pageSize = 25;

  get expertReviewAreasList(): Array<ExpertReviewAreaObject> {
    return this.items;
  }

  get expertReviewAreaDetails(): ExpertReviewAreaDetailsObject {
    return this.details;
  }

  get expertReviewDetailsStatus(): ExpertReviewDetailsStatus {
    return this.detailsStatus;
  }

  get expertReviewRejectReasons(): Array<ExpertReviewRejectReasonObject> {
    return this.rejectReasons;
  }

  get expertReviewReasonsStatus(): ExpertReviewReasonsStatus {
    return this.reasonsStatus;
  }

  get expertReviewCounts(): ExpertReviewCounts {
    return this.counts;
  }

  get expertReviewQuarter(): ExpertReviewQuarterObject {
    return this.quarter;
  }

  get expertReviewFilter(): ExpertReviewFilterKey {
    return this.filter;
  }

  get expertReviewQuarterStatus(): ExpertReviewQuarterStatus {
    return this.quarterStatus;
  }

  get expertReviewTotalItems(): number {
    return this.totalItems;
  }

  get expertReviewCurrentPage(): number {
    return this.currentPage;
  }

  get expertReviewPageSize(): number {
    return this.pageSize;
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_AREAS](payload: ExpertReviewListingResponse) {
    this.items = payload.data;
    this.counts = payload.counts;
    this.quarter = payload.quarter;
    this.totalItems = payload.totalItems;
    this.currentPage = payload.currentPage;
    this.pageSize = payload.pageSize;
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_AREA_DETAILS](
    details: ExpertReviewAreaDetailsObject
  ) {
    this.details = details;
    this.detailsStatus = 'ready';
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_DETAILS_STATUS](
    status: ExpertReviewDetailsStatus
  ) {
    this.detailsStatus = status;
    if (status !== 'ready') {
      this.details = {
        area: null,
        priceHistory: [],
        deals: [],
        clusterDeals: [],
      };
    }
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_REJECT_REASONS](
    reasons: Array<ExpertReviewRejectReasonObject>
  ) {
    this.rejectReasons = reasons;
    this.reasonsStatus = 'ready';
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_REASONS_STATUS](
    status: ExpertReviewReasonsStatus
  ) {
    this.reasonsStatus = status;
    if (status !== 'ready') this.rejectReasons = [];
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_PAGE](page: number) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_FILTER](filter: ExpertReviewFilterKey) {
    this.filter = filter;
    this.currentPage = 1;
  }

  @Mutation
  [Mutations.SET_EXPERT_REVIEW_QUARTER_STATUS](
    quarterStatus: ExpertReviewQuarterStatus
  ) {
    this.quarterStatus = quarterStatus;
    this.currentPage = 1;
  }

  @Action
  [Actions.GET_EXPERT_REVIEW_AREAS](period?: {
    year: number;
    quarter: number;
  }) {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/indices/expert-review/areas', {
      params: {
        ...FILTER_PARAMS[this.filter],
        quarterStatus: this.quarterStatus,
        page: this.currentPage,
        limit: this.pageSize,
        // no period means the quarter open for review
        ...(period ?? {}),
      },
    })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_EXPERT_REVIEW_AREAS, data);
      })
      .catch((error) => {
        commitRequestError(this.context, error);
        throw error;
      });
  }

  @Action
  [Actions.GET_EXPERT_REVIEW_AREA_DETAILS](id: number) {
    ApiService.setAuthorizationHeader();
    const request = ++detailsRequest;
    this.context.commit(Mutations.SET_EXPERT_REVIEW_DETAILS_STATUS, 'loading');
    return ApiService.get('/indices/expert-review/areas', String(id))
      .then(({ data }) => {
        if (request !== detailsRequest) return;
        this.context.commit(
          Mutations.SET_EXPERT_REVIEW_AREA_DETAILS,
          data.data
        );
      })
      .catch((error) => {
        if (request !== detailsRequest) return;
        this.context.commit(
          Mutations.SET_EXPERT_REVIEW_DETAILS_STATUS,
          'error'
        );
        console.error(error);
      });
  }

  @Action
  [Actions.GET_EXPERT_REVIEW_REJECT_REASONS]() {
    ApiService.setAuthorizationHeader();
    this.context.commit(Mutations.SET_EXPERT_REVIEW_REASONS_STATUS, 'loading');
    return ApiService.query('/indices/expert-review/reject-reasons', {})
      .then(({ data }) => {
        this.context.commit(
          Mutations.SET_EXPERT_REVIEW_REJECT_REASONS,
          data.data
        );
      })
      .catch((error) => {
        this.context.commit(
          Mutations.SET_EXPERT_REVIEW_REASONS_STATUS,
          'error'
        );
        console.error(error);
      });
  }

  @Action
  [Actions.GET_PPM_NOTIFICATION_LEVELS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/indices/expert-review/notification-levels')
      .then(({ data }) => data.data)
      .catch((error) => {
        commitRequestError(this.context, error);
        throw error;
      });
  }

  @Action
  [Actions.UPDATE_PPM_ESCALATION_HOURS](payload) {
    ApiService.setAuthorizationHeader();
    return ApiService.update(
      '/indices/expert-review/notification-levels',
      'escalation-hours',
      payload
    )
      .then(({ data }) => data.data)
      .catch((error) => {
        commitRequestError(this.context, error);
        throw error;
      });
  }

  @Action
  [Actions.SET_PPM_RECIPIENT_LEVEL]({ userId, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update(
      '/indices/expert-review/notification-levels/recipients',
      String(userId),
      data
    )
      .then(({ data }) => data.data)
      .catch((error) => {
        commitRequestError(this.context, error);
        throw error;
      });
  }

  @Action
  [Actions.APPROVE_EXPERT_REVIEW_AREAS](payload) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/indices/expert-review/areas/approve', payload)
      .then(({ data }) => data.data)
      .catch((error) => {
        commitRequestError(this.context, error);
        throw error;
      });
  }

  @Action
  [Actions.APPROVE_LOW_RISK_EXPERT_REVIEW_AREAS](payload) {
    ApiService.setAuthorizationHeader();
    return ApiService.post(
      '/indices/expert-review/areas/approve-low-risk',
      payload
    )
      .then(({ data }) => data.data)
      .catch((error) => {
        commitRequestError(this.context, error);
        throw error;
      });
  }

  @Action
  [Actions.REJECT_EXPERT_REVIEW_AREA]({ id, ...decision }) {
    ApiService.setAuthorizationHeader();
    return ApiService.post(
      `/indices/expert-review/areas/${id}/reject`,
      decision
    )
      .then(({ data }) => data.data)
      .catch((error) => {
        commitRequestError(this.context, error);
        throw error;
      });
  }

  @Action
  [Actions.UPDATE_EXPERT_REVIEW_PAGE](page: number) {
    const previousPage = this.currentPage;
    this.context.commit(Mutations.SET_EXPERT_REVIEW_PAGE, page);
    return this.context
      .dispatch(Actions.GET_EXPERT_REVIEW_AREAS)
      .catch((error) => {
        this.context.commit(Mutations.SET_EXPERT_REVIEW_PAGE, previousPage);
        throw error;
      });
  }

  @Action
  [Actions.UPDATE_EXPERT_REVIEW_QUARTER_STATUS](
    quarterStatus: ExpertReviewQuarterStatus
  ) {
    const previousQuarterStatus = this.quarterStatus;
    const previousPage = this.currentPage;
    this.context.commit(
      Mutations.SET_EXPERT_REVIEW_QUARTER_STATUS,
      quarterStatus
    );
    return this.context
      .dispatch(Actions.GET_EXPERT_REVIEW_AREAS)
      .catch((error) => {
        this.context.commit(
          Mutations.SET_EXPERT_REVIEW_QUARTER_STATUS,
          previousQuarterStatus
        );
        this.context.commit(Mutations.SET_EXPERT_REVIEW_PAGE, previousPage);
        throw error;
      });
  }

  @Action
  [Actions.UPDATE_EXPERT_REVIEW_FILTER](filter: ExpertReviewFilterKey) {
    const previousFilter = this.filter;
    const previousPage = this.currentPage;
    this.context.commit(Mutations.SET_EXPERT_REVIEW_FILTER, filter);
    return this.context
      .dispatch(Actions.GET_EXPERT_REVIEW_AREAS)
      .catch((error) => {
        this.context.commit(Mutations.SET_EXPERT_REVIEW_FILTER, previousFilter);
        this.context.commit(Mutations.SET_EXPERT_REVIEW_PAGE, previousPage);
        throw error;
      });
  }
}
